// Edge function client for EDPR document preparation.
// Wraps the edpr-prepare-documents dispatcher which enqueues a job and
// forwards the request to the edpr-preparer Cloud Run service.
//
// The underlying edge function returns `{ job_id, ok: true }` (with `{ error }`
// on failure) rather than the standard `{ success, data }` EdgeFnResponse
// shape, so this method throws on error and returns the success fields
// directly — same convention as CompanyLookupEdgeFnClient.

import type { SupabaseClient } from '../base.ts';
import type { FunctionsInvoker } from './BaseEdgeFnClient.ts';

/** Raw body the dispatcher returns: `{ job_id, ok }` on success, `{ error, code, failures }` on failure. */
interface EdprPrepareEnvelope {
  job_id?: string;
  ok?: boolean;
  error?: string;
  code?: string;
  failures?: unknown;
}

// ── Result types ────────────────────────────────────────────────────────

export type EdprBranch = 'invoices' | 'ie599' | 'annex7';

export interface EdprTransactionError {
  branch: EdprBranch;
  transaction_id: number;
  fakturownia_id?: number;
  drive_file_id?: string;
  reason: string;
  code?: string;
}

export interface EdprPrepareResult {
  /** UUID of the jobs row tracking the preparation run. */
  job_id: string;
  /** Legacy success flag from the dispatcher — always true on resolved response. */
  ok: boolean;
}

/**
 * Thrown when the Cloud Run preparer couldn't fetch source files or otherwise
 * prepare one or more transactions. Carries the full per-transaction failure
 * list so the UI can render which transactions to fix.
 */
export class EdprPrepareFailedError extends Error {
  readonly failures: EdprTransactionError[];
  readonly code = 'EDPR_PREPARE_FAILED' as const;
  constructor(message: string, failures: EdprTransactionError[]) {
    super(message);
    this.name = 'EdprPrepareFailedError';
    this.failures = failures;
  }
}

// ── Client ──────────────────────────────────────────────────────────────

export class EdprEdgeFnClient {
  constructor(private readonly sb: SupabaseClient) {}

  /**
   * Kick off EDPR document preparation for the given edpr_documents row.
   * Creates a jobs row and awaits the Cloud Run preparer completion.
   *
   * Throws `EdprPrepareFailedError` when one or more transactions couldn't be
   * prepared (missing PDFs, Fakturownia 404, etc.) — callers should render
   * `.failures` to the operator. Throws a plain `Error` for transport/infra
   * failures.
   */
  async prepareDocuments(edprDocumentId: number): Promise<EdprPrepareResult> {
    const invoker = this.sb as unknown as FunctionsInvoker<EdprPrepareEnvelope>;
    const { data, error } = await invoker.functions.invoke('edpr-prepare-documents', {
      body: { edpr_document_id: edprDocumentId },
    });
    if (error) throw new Error(error.message || 'EDPR prepare failed');
    if (data && data.code === 'EDPR_PREPARE_FAILED') {
      throw new EdprPrepareFailedError(
        data.error || 'EDPR preparation failed',
        Array.isArray(data.failures) ? (data.failures as EdprTransactionError[]) : [],
      );
    }
    if (data?.error) throw new Error(data.error);
    return data as unknown as EdprPrepareResult;
  }
}
